import { Box, Choicebox, FormGroup } from '@ksyos/design-system';
import type { ValidationErrors } from '@frontend/utils';
import type {
    IOrthOProcedureCustomControlData,
    IVisusBcvaFields,
    IVisusBcvaRefraction,
    IVisusBcvaWithCorrection,
    IVisusCcFields,
    IVisusCcWithCorrectionFields,
    IVisusCcWithRefractionFields,
    IVisusEcFields,
    IVisusEcRefraction,
    IVisusEcWithCorrection,
    IVisusScFields,
} from '../../../../../common/interfaces/ophthalmology/orthO/orthOProcedureCustomControlData';
import type { IOrthOV1ProcedureExaminationsProps } from './OrthOV1ProcedureExaminations';
import { FormValidationMessage } from '../../../elements/FormValidationMessage';
import { OrthOExaminationOrder } from './orthOExaminationOrder';
import { OrthOV1ProcedureCheckedVisusExaminationOdOsRow } from './OrthOV1ProcedureCheckedVisusExaminationOdOsRow';
import { getInitialVisusFields } from './orthOV1ProcedureInitializeCustomControlData';
import { OrthOV1ProcedureOdOsOdsFindingsRow } from './OrthOV1ProcedureOdOsOdsFindingsRow';

interface IVisusExaminations {
    correction: string;
    label: string;
    visusGroup: keyof IOrthOProcedureCustomControlData['visusGroup'];
}

const visusExaminations: IVisusExaminations[] = [
    {
        label: 'Visus SC',
        correction: 'zonder correctie',
        visusGroup: 'visus_sc',
    },
    {
        label: 'Visus CC',
        correction: 'met correctie',
        visusGroup: 'visus_cc',
    },
    {
        label: 'Visus BCVA',
        correction: 'optimale correctie',
        visusGroup: 'visus_bcva',
    },
    {
        label: 'Visus EC',
        correction: 'eigen correctie',
        visusGroup: 'visus_ec',
    },
];

const visus_ec_own_correction_fields: (keyof IVisusEcWithCorrection)[] = ['visus_ec_od', 'visus_ec_os', 'visus_ec_ods'];

export const visus_ec_refraction_fields: (keyof IVisusEcRefraction)[] = [
    'visus_ec_refractie_sferisch_od',
    'visus_ec_refractie_sferisch_os',
    'visus_ec_refractie_cylindrisch_od',
    'visus_ec_refractie_cylindrisch_os',
    'visus_ec_refractie_as_od',
    'visus_ec_refractie_as_os',
    'visus_ec_refractie_additie_od',
    'visus_ec_refractie_additie_os',
    'visus_ec_refractie_prisma_od',
    'visus_ec_refractie_prisma_os',
    'visus_ec_refractie_prisma_degrees_od',
    'visus_ec_refractie_prisma_degrees_os',
];

export const visus_ec_fields: (keyof IVisusEcFields)[] = [...visus_ec_own_correction_fields];

export const visus_sc_fields: (keyof IVisusScFields)[] = ['visus_sc_od', 'visus_sc_os', 'visus_sc_ods'];

const visus_cc_with_correction_fields: (keyof IVisusCcWithCorrectionFields)[] = [
    'visus_cc_od',
    'visus_cc_os',
    'visus_cc_ods',
];

const visus_cc_refraction_fields: (keyof IVisusCcWithRefractionFields)[] = [
    'visus_cc_refractie_sferisch_od',
    'visus_cc_refractie_sferisch_os',
    'visus_cc_refractie_cylindrisch_od',
    'visus_cc_refractie_cylindrisch_os',
    'visus_cc_refractie_as_od',
    'visus_cc_refractie_as_os',
    'visus_cc_refractie_additie_od',
    'visus_cc_refractie_additie_os',
    'visus_cc_refractie_prisma_od',
    'visus_cc_refractie_prisma_os',
    'visus_cc_refractie_prisma_degrees_od',
    'visus_cc_refractie_prisma_degrees_os',
];

export const visus_cc_fields: (keyof IVisusCcFields)[] = [
    ...visus_cc_with_correction_fields,
    ...visus_cc_refraction_fields,
];

const visus_bvca_with_optimal_correction_fields: (keyof IVisusBcvaWithCorrection)[] = [
    'visus_bcva_od',
    'visus_bcva_os',
    'visus_bcva_ods',
];

const visus_bcva_refraction_fields: (keyof IVisusBcvaRefraction)[] = [
    'visus_bcva_refractie_sferisch_od',
    'visus_bcva_refractie_sferisch_os',
    'visus_bcva_refractie_cylindrisch_od',
    'visus_bcva_refractie_cylindrisch_os',
    'visus_bcva_refractie_as_od',
    'visus_bcva_refractie_as_os',
    'visus_bcva_refractie_additie_od',
    'visus_bcva_refractie_additie_os',
    'visus_bcva_refractie_prisma_od',
    'visus_bcva_refractie_prisma_os',
    'visus_bcva_refractie_prisma_degrees_od',
    'visus_bcva_refractie_prisma_degrees_os',
];

export const visus_bvca_fields: (keyof IVisusBcvaFields)[] = [
    ...visus_bvca_with_optimal_correction_fields,
    ...visus_bcva_refraction_fields,
];

export function OrthOV1ProcedureCheckedVisusExaminations(props: IOrthOV1ProcedureExaminationsProps) {
    const { customControlData, isReadOnly, errors, handleChangeCustomControl, validateOneCustomControl } = props;

    return (
        <>
            {!isReadOnly && (
                <FormGroup
                    hasErrors={errors && errors.has('customControlData.visusGroup')}
                    isRequired
                    label="Visus"
                >
                    <Box
                        display="flex"
                        flexDirection="row"
                        flexWrap="wrap"
                        gap={3}
                    >
                        {visusExaminations.map((examination) => {
                            return (
                                <Choicebox
                                    checked={customControlData.visusGroup[examination.visusGroup]}
                                    key={examination.visusGroup}
                                    label={`${examination.label} (${examination.correction})`}
                                    name={examination.label}
                                    onChange={async (event) =>
                                        await handleChangeVisusGroup(examination.visusGroup, event.target.checked)
                                    }
                                />
                            );
                        })}
                        <FormValidationMessage error={errors && errors.get('customControlData.visusGroup')} />
                    </Box>
                </FormGroup>
            )}

            {customControlData.visusGroup.visus_sc && (
                <OrthOV1ProcedureOdOsOdsFindingsRow
                    errors={isReadOnly ? undefined : errors}
                    field="visus_sc"
                    hasErrors={anyExaminationFieldHasErrors(visus_sc_fields, isReadOnly, errors)}
                    isReadOnly={isReadOnly}
                    isRequired
                    label="Visus SC (zonder correctie)"
                    tabIndexOd={OrthOExaminationOrder.checkedVisionExaminationOd}
                    tabIndexOds={OrthOExaminationOrder.checkedVisionExaminationOds}
                    tabIndexOs={OrthOExaminationOrder.checkedVisionExaminationOs}
                    value_od={customControlData.visus_sc_od}
                    value_ods={customControlData.visus_sc_ods}
                    value_os={customControlData.visus_sc_os}
                />
            )}

            {customControlData.visusGroup.visus_cc && (
                <>
                    <OrthOV1ProcedureOdOsOdsFindingsRow
                        errors={isReadOnly ? undefined : errors}
                        field="visus_cc"
                        hasErrors={anyExaminationFieldHasErrors(visus_cc_with_correction_fields, isReadOnly, errors)}
                        isReadOnly={isReadOnly}
                        isRequired
                        label="Visus CC (met correctie)"
                        tabIndexOd={OrthOExaminationOrder.checkedVisionExaminationOd}
                        tabIndexOds={OrthOExaminationOrder.checkedVisionExaminationOds}
                        tabIndexOs={OrthOExaminationOrder.checkedVisionExaminationOs}
                        value_od={customControlData.visus_cc_od}
                        value_ods={customControlData.visus_cc_ods}
                        value_os={customControlData.visus_cc_os}
                    />

                    <OrthOV1ProcedureCheckedVisusExaminationOdOsRow
                        additie_od={customControlData.visus_cc_refractie_additie_od}
                        additie_os={customControlData.visus_cc_refractie_additie_os}
                        as_od={customControlData.visus_cc_refractie_as_od}
                        as_os={customControlData.visus_cc_refractie_as_os}
                        cylindrisch_od={customControlData.visus_cc_refractie_cylindrisch_od}
                        cylindrisch_os={customControlData.visus_cc_refractie_cylindrisch_os}
                        errors={isReadOnly ? undefined : errors}
                        field="visus_cc_refractie"
                        hasErrors={anyExaminationFieldHasErrors(visus_cc_refraction_fields, isReadOnly, errors)}
                        isReadOnly={isReadOnly}
                        isRequired
                        label="Visus CC refractie"
                        onDoValidate={validateOneCustomControl}
                        prisma_degrees_od={customControlData.visus_cc_refractie_prisma_degrees_od}
                        prisma_degrees_os={customControlData.visus_cc_refractie_prisma_degrees_os}
                        prisma_od={customControlData.visus_cc_refractie_prisma_od}
                        prisma_os={customControlData.visus_cc_refractie_prisma_os}
                        sferisch_od={customControlData.visus_cc_refractie_sferisch_od}
                        sferisch_os={customControlData.visus_cc_refractie_sferisch_os}
                    />
                </>
            )}

            {customControlData.visusGroup.visus_bcva && (
                <>
                    <OrthOV1ProcedureOdOsOdsFindingsRow
                        errors={isReadOnly ? undefined : errors}
                        field="visus_bcva"
                        hasErrors={anyExaminationFieldHasErrors(
                            visus_bvca_with_optimal_correction_fields,
                            isReadOnly,
                            errors
                        )}
                        isReadOnly={isReadOnly}
                        isRequired
                        label="Visus BCVA (optimale correctie)"
                        tabIndexOd={OrthOExaminationOrder.checkedVisionExaminationOd}
                        tabIndexOds={OrthOExaminationOrder.checkedVisionExaminationOds}
                        tabIndexOs={OrthOExaminationOrder.checkedVisionExaminationOs}
                        value_od={customControlData.visus_bcva_od}
                        value_ods={customControlData.visus_bcva_ods}
                        value_os={customControlData.visus_bcva_os}
                    />

                    <OrthOV1ProcedureCheckedVisusExaminationOdOsRow
                        additie_od={customControlData.visus_bcva_refractie_additie_od}
                        additie_os={customControlData.visus_bcva_refractie_additie_os}
                        as_od={customControlData.visus_bcva_refractie_as_od}
                        as_os={customControlData.visus_bcva_refractie_as_os}
                        cylindrisch_od={customControlData.visus_bcva_refractie_cylindrisch_od}
                        cylindrisch_os={customControlData.visus_bcva_refractie_cylindrisch_os}
                        errors={isReadOnly ? undefined : errors}
                        field="visus_bcva_refractie"
                        hasErrors={anyExaminationFieldHasErrors(visus_bcva_refraction_fields, isReadOnly, errors)}
                        isReadOnly={isReadOnly}
                        isRequired
                        label="Visus BCVA refractie"
                        onDoValidate={validateOneCustomControl}
                        prisma_degrees_od={customControlData.visus_bcva_refractie_prisma_degrees_od}
                        prisma_degrees_os={customControlData.visus_bcva_refractie_prisma_degrees_os}
                        prisma_od={customControlData.visus_bcva_refractie_prisma_od}
                        prisma_os={customControlData.visus_bcva_refractie_prisma_os}
                        sferisch_od={customControlData.visus_bcva_refractie_sferisch_od}
                        sferisch_os={customControlData.visus_bcva_refractie_sferisch_os}
                    />
                </>
            )}

            {customControlData.visusGroup.visus_ec && (
                <>
                    <OrthOV1ProcedureOdOsOdsFindingsRow
                        errors={isReadOnly ? undefined : errors}
                        field="visus_ec"
                        hasErrors={anyExaminationFieldHasErrors(visus_ec_own_correction_fields, isReadOnly, errors)}
                        isReadOnly={isReadOnly}
                        isRequired
                        label="Visus EC (eigen correctie)"
                        tabIndexOd={OrthOExaminationOrder.checkedVisionExaminationOd}
                        tabIndexOds={OrthOExaminationOrder.checkedVisionExaminationOds}
                        tabIndexOs={OrthOExaminationOrder.checkedVisionExaminationOs}
                        value_od={customControlData.visus_ec_od}
                        value_ods={customControlData.visus_ec_ods}
                        value_os={customControlData.visus_ec_os}
                    />

                    <OrthOV1ProcedureCheckedVisusExaminationOdOsRow
                        additie_od={customControlData.visus_ec_refractie_additie_od}
                        additie_os={customControlData.visus_ec_refractie_additie_os}
                        as_od={customControlData.visus_ec_refractie_as_od}
                        as_os={customControlData.visus_ec_refractie_as_os}
                        cylindrisch_od={customControlData.visus_ec_refractie_cylindrisch_od}
                        cylindrisch_os={customControlData.visus_ec_refractie_cylindrisch_os}
                        errors={isReadOnly ? undefined : errors}
                        field="visus_ec_refractie"
                        hasErrors={anyExaminationFieldHasErrors(visus_ec_refraction_fields, isReadOnly, errors)}
                        isReadOnly={isReadOnly}
                        isRequired
                        label="Visus EC refractie"
                        onDoValidate={validateOneCustomControl}
                        prisma_degrees_od={customControlData.visus_ec_refractie_prisma_degrees_od}
                        prisma_degrees_os={customControlData.visus_ec_refractie_prisma_degrees_os}
                        prisma_od={customControlData.visus_ec_refractie_prisma_od}
                        prisma_os={customControlData.visus_ec_refractie_prisma_os}
                        sferisch_od={customControlData.visus_ec_refractie_sferisch_od}
                        sferisch_os={customControlData.visus_ec_refractie_sferisch_os}
                    />
                </>
            )}
        </>
    );

    async function handleChangeVisusGroup(name: IVisusExaminations['visusGroup'], value: boolean) {
        const examinationFields = value === false ? getInitialVisusFields(name) : {};

        if (handleChangeCustomControl) {
            await handleChangeCustomControl({
                visusGroup: {
                    ...customControlData.visusGroup,
                    [name]: value,
                },
                ...examinationFields,
            });
            if (validateOneCustomControl) {
                validateOneCustomControl('visusGroup');
            }
        }
    }
}

export function anyExaminationFieldHasErrors(fields: string[], isReadOnly: boolean, errors?: ValidationErrors) {
    if (!isReadOnly && errors) {
        return fields
            .map((field) => {
                return errors.has(`customControlData.${field}`);
            })
            .some((value) => value);
    }

    return false;
}
